import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { UnitGroup } from "~/applications/OfferSearch/Domain/UnitGroup";
import { contentStudioClient } from "~/clients/ContentStudio";
import { TimeOfDay } from "~/core/TimeOfDay";
import { SanityImageWithMetadataPayloadSchema } from "~/core/api/Sanity/asset";
import {
  SanityLocalizedFieldSchema,
  localizeField,
  localizedFieldsProjection,
} from "~/core/api/Sanity/localize";
import { buildGroqQuery } from "~/core/api/Sanity/query";
import { AVAILABLE_LOCALES } from "~/core/locale";

const getUnitGroupsByHotelIdsQuery = (locale: AVAILABLE_LOCALES) =>
  buildGroqQuery(`
  *[
    _type == 'unitGroup'
    && pmsId != null
    && loge._ref in $hotelIds
    && !(_id in path('drafts.**'))
  ]{
    pmsId{ current },
    loge->{
      pmsId{ current },
      name,
      building->{
        address{
          line1,
          line2,
          postalCode,
          city,
          coordinate{ lat, lng }
        }
      },
      "logePolicy": hotelPolicy {
        arrivalTime,
        departureTime
      },
      "logePage": (
        *[_type == "logePage" && references(^._id) && !(_id in path('drafts.**'))][0]{
          seoSlug{ current }
        }
      )
    },
    seoSlug{ current },
    name,
    'description': coalesce(description{ ${localizedFieldsProjection(
      locale,
    )} }, description),
    gallery[]{
      asset->{
        url,
        metadata {
          lqip,
          dimensions {
            height,
            width
          },
        },
      },
      caption{ ${localizedFieldsProjection(locale)} }
    },
    numberOfRooms,
    hasKitchen,
    hasLivingRoom,
    hasConvertibleCouch,
    maxCapacity,
    numberOfBathrooms,
    numberOfReducedMobilityBathrooms,
    surface { surfaceMax, surfaceMin },
    highlightConnectingDoor
  }
`);

const UnitGroupOfHotelsPayloadSchema = z.array(
  z.object({
    pmsId: z.object({ current: z.string() }),
    seoSlug: z.object({ current: z.string() }),
    loge: z.object({
      pmsId: z.object({ current: z.string() }),
      name: z.string(),
      building: z.object({
        address: z.object({
          line1: z.string(),
          line2: z.string().nullable(),
          postalCode: z.string(),
          city: z.string(),
          coordinate: z.object({
            lat: z.number(),
            lng: z.number(),
          }),
        }),
      }),
      logePolicy: z.object({
        arrivalTime: z.string(),
        departureTime: z.string(),
      }),
      logePage: z.object({
        seoSlug: z.object({ current: z.string() }),
      }),
    }),
    name: z.string(),
    description: z.string().or(SanityLocalizedFieldSchema),
    gallery: z.array(
      z.object({
        asset: SanityImageWithMetadataPayloadSchema,
        caption: SanityLocalizedFieldSchema,
      }),
    ),
    numberOfRooms: z.number(),
    hasKitchen: z.boolean(),
    hasLivingRoom: z.boolean(),
    hasConvertibleCouch: z.boolean(),
    maxCapacity: z.number(),
    numberOfBathrooms: z.number(),
    numberOfReducedMobilityBathrooms: z.number(),
    surface: z.object({
      surfaceMin: z.number(),
      surfaceMax: z.number(),
    }),
    highlightConnectingDoor: z.boolean(),
  }),
);

type FetchUnitGroupsOfHotelsQuery = {
  locale: AVAILABLE_LOCALES;
  hotelIds: Array<string>;
};

const fetchUnitGroupsOfHotelsKey = (
  query: FetchUnitGroupsOfHotelsQuery,
): [string, FetchUnitGroupsOfHotelsQuery] => [
  "offer-search-unit-groups-of-hotels",
  query,
];

export const fetchUnitGroupsOfHotels = async ({
  locale,
  hotelIds,
}: FetchUnitGroupsOfHotelsQuery): Promise<Array<UnitGroup>> => {
  const response = await contentStudioClient.fetch(
    getUnitGroupsByHotelIdsQuery(locale),
    { hotelIds },
  );

  const unitGroups = UnitGroupOfHotelsPayloadSchema.parse(response);

  return unitGroups.map((oneUnitGroupPayload) => {
    return {
      id: oneUnitGroupPayload.pmsId.current,
      slug: oneUnitGroupPayload.seoSlug.current,
      loge: {
        id: oneUnitGroupPayload.loge.pmsId.current,
        slug: oneUnitGroupPayload.loge.logePage.seoSlug.current,
        name: oneUnitGroupPayload.loge.name,
        address: {
          line1: oneUnitGroupPayload.loge.building.address.line1,
          ...(oneUnitGroupPayload.loge.building.address.line2 !== null
            ? { line2: oneUnitGroupPayload.loge.building.address.line2 }
            : {}),
          postalCode: oneUnitGroupPayload.loge.building.address.postalCode,
          city: oneUnitGroupPayload.loge.building.address.city,
          coordinate: {
            latitude: oneUnitGroupPayload.loge.building.address.coordinate.lat,
            longitude: oneUnitGroupPayload.loge.building.address.coordinate.lng,
          },
        },
        policy: {
          arrivalTime: TimeOfDay.fromStringTime(
            oneUnitGroupPayload.loge.logePolicy.arrivalTime,
          ).asHours(),
          departureTime: TimeOfDay.fromStringTime(
            oneUnitGroupPayload.loge.logePolicy.departureTime,
          ).asHours(),
        },
      },
      pmsId: oneUnitGroupPayload.pmsId.current,
      name: oneUnitGroupPayload.name,
      description:
        typeof oneUnitGroupPayload.description === "string"
          ? oneUnitGroupPayload.description
          : localizeField(oneUnitGroupPayload.description, locale),
      images: oneUnitGroupPayload.gallery.map((oneImage) => ({
        ...oneImage,
        caption: localizeField(oneImage.caption, locale),
      })),
      numberOfRooms: oneUnitGroupPayload.numberOfRooms,
      hasKitchen: oneUnitGroupPayload.hasKitchen,
      hasLivingRoom: oneUnitGroupPayload.hasLivingRoom,
      hasConvertibleCouch: oneUnitGroupPayload.hasConvertibleCouch,
      maxCapacity: oneUnitGroupPayload.maxCapacity,
      numberOfBathrooms: oneUnitGroupPayload.numberOfBathrooms,
      numberOfReducedMobilityBathrooms:
        oneUnitGroupPayload.numberOfReducedMobilityBathrooms,
      surface: {
        minimum: oneUnitGroupPayload.surface.surfaceMin,
        maximum: oneUnitGroupPayload.surface.surfaceMax,
      },
      highlightConnectingDoor: oneUnitGroupPayload.highlightConnectingDoor,
    };
  });
};

export const useUnitGroupsOfHotels = (
  query: FetchUnitGroupsOfHotelsQuery | false,
): { unitGroups: Array<UnitGroup> | null } => {
  const { data: unitGroups = null } = useQuery(
    query ? fetchUnitGroupsOfHotelsKey(query) : [],
    () => (query ? fetchUnitGroupsOfHotels(query) : null),
    { enabled: query !== false },
  );

  return { unitGroups };
};
